import { useState, useEffect } from 'react';
import { useSite } from '@/contexts/SiteContext';
import {
  valueEvidenceService,
  type ValueEvidenceReport,
  type ValueEvidenceDimension,
  type StoredReport
} from '@/services/valueEvidenceService';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import {
  TrendingUp, TrendingDown, AlertCircle, RefreshCw, Clock,
  ArrowUpRight, ArrowDownRight, Target, Shield, Eye, DollarSign,
  BarChart2, Zap, Layers, Trophy, Bot
} from 'lucide-react';

const DIMENSION_CONFIG: Record<string, { icon: any; color: string; shortName: string }> = {
  'Before/After Content Deployment': { icon: TrendingUp, color: 'text-green-600', shortName: 'Before/After' },
  'Semantic Value Improvement': { icon: Target, color: 'text-blue-600', shortName: 'Semantic Value' },
  'Market Demand Coverage': { icon: Layers, color: 'text-purple-600', shortName: 'Demand Coverage' },
  'Competitive Positioning': { icon: Shield, color: 'text-orange-600', shortName: 'Competitive' },
  'AI/Bot Attention': { icon: Bot, color: 'text-cyan-600', shortName: 'AI Attention' },
  'Revenue Attribution': { icon: DollarSign, color: 'text-emerald-600', shortName: 'Revenue' },
  'Content Quality Trends': { icon: BarChart2, color: 'text-indigo-600', shortName: 'Quality Trends' },
  'Paid/Organic Efficiency': { icon: Zap, color: 'text-amber-600', shortName: 'Paid/Organic' },
  'Demand Velocity': { icon: ArrowUpRight, color: 'text-rose-600', shortName: 'Demand Velocity' },
  'Category Impact': { icon: Trophy, color: 'text-violet-600', shortName: 'Category Impact' }
};

function DimensionCard({ dimension }: { dimension: ValueEvidenceDimension }) {
  const config = DIMENSION_CONFIG[dimension.dimensionName] || {
    icon: BarChart2, color: 'text-gray-600', shortName: dimension.dimensionName
  };
  const Icon = config.icon;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Icon className={`h-5 w-5 ${config.color}`} />
          <CardTitle className="text-base">{config.shortName}</CardTitle>
        </div>
        <CardDescription className="text-xs">
          {dimension.dataPoints} data points
        </CardDescription>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-3">{dimension.summary}</p>
        <DimensionDetails dimension={dimension} />
      </CardContent>
    </Card>
  );
}

function DimensionDetails({ dimension }: { dimension: ValueEvidenceDimension }) {
  const { data } = dimension;
  const name = dimension.dimensionName;

  if (name === 'Before/After Content Deployment') {
    return (
      <div className="space-y-2">
        <MetricRow label="Pages deployed" value={data.deployedPages} />
        <MetricRow label="With GSC data" value={data.pagesWithGSCData} />
        <MetricRow label="Avg position delta" value={data.avgPositionDelta?.toFixed(1)} trend={data.avgPositionDelta > 0 ? 'up' : 'down'} />
        <MetricRow label="Clicks before" value={data.clicksBefore?.toLocaleString()} />
        <MetricRow label="Clicks after" value={data.clicksAfter?.toLocaleString()} trend={data.clicksDelta > 0 ? 'up' : 'down'} />
      </div>
    );
  }

  if (name === 'Market Demand Coverage') {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Coverage</span>
          <span className="font-medium">{data.coverageRate}%</span>
        </div>
        <Progress value={data.coverageRate} className="h-2" />
        <MetricRow label="Covered clusters" value={`${data.coveredClusters} / ${data.totalClusters}`} />
        {data.uncoveredValueEur > 0 && (
          <MetricRow label="Uncovered value" value={`\u20AC${Math.round(data.uncoveredValueEur).toLocaleString()}`} />
        )}
      </div>
    );
  }

  if (name === 'Paid/Organic Efficiency') {
    return (
      <div className="space-y-2">
        <div className="text-2xl font-bold text-emerald-600">
          {'\u20AC'}{data.totalReclaimableEurPerMonth?.toLocaleString()}/mo
        </div>
        <p className="text-xs text-muted-foreground">Reclaimable ad spend</p>
        <MetricRow label="Overlapping keywords" value={data.overlappingKeywords} />
        {data.topOpportunities?.slice(0, 3).map((opp: any, i: number) => (
          <div key={i} className="text-xs flex justify-between border-t pt-1">
            <span className="truncate max-w-[60%]">{opp.query}</span>
            <span className="text-emerald-600">{'\u20AC'}{Math.round(opp.reclaimableEurPerMonth)}/mo</span>
          </div>
        ))}
      </div>
    );
  }

  if (name === 'Revenue Attribution') {
    const withAeo = data.with_aeo || {};
    const withoutAeo = data.without_aeo || {};
    return (
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="bg-emerald-50 rounded p-2">
            <div className="text-xs text-muted-foreground">With AEO</div>
            <div className="font-bold">{'\u20AC'}{Math.round(withAeo.revenue || 0).toLocaleString()}</div>
            <div className="text-xs">{(withAeo.sessions || 0).toLocaleString()} sessions</div>
          </div>
          <div className="bg-gray-50 rounded p-2">
            <div className="text-xs text-muted-foreground">Without AEO</div>
            <div className="font-bold">{'\u20AC'}{Math.round(withoutAeo.revenue || 0).toLocaleString()}</div>
            <div className="text-xs">{(withoutAeo.sessions || 0).toLocaleString()} sessions</div>
          </div>
        </div>
      </div>
    );
  }

  if (name === 'AI/Bot Attention') {
    const bots = data.botVisitsByName || {};
    return (
      <div className="space-y-2">
        <MetricRow label="Bot types detected" value={Object.keys(bots).length} />
        <MetricRow label="LLM mentions" value={data.mentionCount} />
        {data.avgAccuracyScore && (
          <MetricRow label="Avg accuracy" value={`${(data.avgAccuracyScore * 100).toFixed(0)}%`} />
        )}
        {Object.entries(bots).slice(0, 4).map(([name, count]) => (
          <div key={name} className="text-xs flex justify-between border-t pt-1">
            <span>{name}</span>
            <span className="font-medium">{(count as number).toLocaleString()} requests</span>
          </div>
        ))}
      </div>
    );
  }

  if (name === 'Competitive Positioning') {
    return (
      <div className="space-y-2">
        <MetricRow label="Competitive score" value={data.avgCompetitiveScore} />
        <MetricRow label="Content depth" value={data.avgContentDepth} />
        <MetricRow label="Schema coverage" value={data.avgSchemaCoverage} />
        <MetricRow label="FAQ coverage" value={data.avgFaqCoverage} />
        <MetricRow label="Pages analyzed" value={data.pagesAnalyzed} />
      </div>
    );
  }

  if (name === 'Content Quality Trends' && data.weeks?.length > 0) {
    const latest = data.weeks[0];
    return (
      <div className="space-y-2">
        <MetricRow label="Total evaluations" value={data.totalEvaluated} />
        <MetricRow label="Latest avg quality" value={latest.avgQuality} />
        <MetricRow label="Latest approval rate" value={`${Math.round(100 * latest.approved / latest.total)}%`} />
      </div>
    );
  }

  if (name === 'Demand Velocity' && data.velocities) {
    return (
      <div className="space-y-2">
        {data.velocities.map((v: any) => (
          <div key={v.label} className="flex justify-between text-sm border-b pb-1">
            <span>{v.label}</span>
            <span className="font-medium">{v.clusters} clusters ({'\u20AC'}{Math.round(v.valueEur).toLocaleString()})</span>
          </div>
        ))}
      </div>
    );
  }

  if (name === 'Category Impact' && data.categories) {
    return (
      <div className="space-y-2">
        <MetricRow label="Total value" value={`\u20AC${data.totalCommercialValueEur?.toLocaleString()}`} />
        {data.categories.slice(0, 4).map((c: any, i: number) => (
          <div key={i} className="text-xs flex justify-between border-t pt-1">
            <span className="truncate max-w-[55%]">{c.categoryPath}</span>
            <span>{c.coveragePct?.toFixed(0)}% covered</span>
          </div>
        ))}
      </div>
    );
  }

  // Fallback: render data keys
  return (
    <div className="space-y-1">
      {Object.entries(data).slice(0, 5).map(([key, val]) => (
        <MetricRow key={key} label={key} value={typeof val === 'object' ? JSON.stringify(val).substring(0, 40) : String(val)} />
      ))}
    </div>
  );
}

function MetricRow({ label, value, trend }: { label: string; value: any; trend?: 'up' | 'down' }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium flex items-center gap-1">
        {value}
        {trend === 'up' && <ArrowUpRight className="h-3 w-3 text-green-500" />}
        {trend === 'down' && <ArrowDownRight className="h-3 w-3 text-red-500" />}
      </span>
    </div>
  );
}

export default function ValueEvidenceDashboard() {
  const { selectedSite } = useSite();
  const siteId = selectedSite?.id || (selectedSite as any)?._id;

  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [report, setReport] = useState<ValueEvidenceReport | null>(null);
  const [storedAt, setStoredAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!siteId) { setLoading(false); return; }
    loadReport();
  }, [siteId]);

  async function loadReport() {
    setLoading(true);
    setError(null);
    try {
      const stored = await valueEvidenceService.getLatestReport(siteId);
      if (stored) {
        const data = typeof stored.report_data === 'string'
          ? JSON.parse(stored.report_data)
          : stored.report_data;
        setReport(data);
        setStoredAt(stored.generated_at);
      } else {
        setReport(null);
        setStoredAt(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }

  async function handleGenerate() {
    setGenerating(true);
    setError(null);
    try {
      const newReport = await valueEvidenceService.generateReport(siteId);
      setReport(newReport);
      setStoredAt(new Date().toISOString());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate report');
    } finally {
      setGenerating(false);
    }
  }

  if (!siteId) {
    return (
      <div className="p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Select a site to view value evidence.</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-96" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Trophy className="h-6 w-6 text-amber-500" />
            Value Evidence
          </h1>
          <p className="text-muted-foreground mt-1">
            Quantified proof of PageMind's impact across 10 evidence dimensions
          </p>
        </div>
        <div className="flex items-center gap-2">
          {storedAt && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {new Date(storedAt).toLocaleDateString()} {new Date(storedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <Button onClick={handleGenerate} disabled={generating} size="sm">
            <RefreshCw className={`h-4 w-4 mr-1 ${generating ? 'animate-spin' : ''}`} />
            {generating ? 'Generating...' : report ? 'Regenerate' : 'Generate Report'}
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Summary bar */}
      {report && (
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div>
                  <div className="text-3xl font-bold">{report.dimensionsCompleted}/{report.dimensionsTotal}</div>
                  <div className="text-xs text-muted-foreground">Dimensions with data</div>
                </div>
                <Progress value={(report.dimensionsCompleted / report.dimensionsTotal) * 100} className="w-32 h-2" />
              </div>
              <div className="flex gap-2 flex-wrap">
                {Object.keys(DIMENSION_CONFIG).map(name => {
                  const hasData = report.dimensions.some(d => d.dimensionName === name);
                  const config = DIMENSION_CONFIG[name];
                  return (
                    <Badge
                      key={name}
                      variant={hasData ? 'default' : 'outline'}
                      className={`text-xs ${hasData ? '' : 'opacity-40'}`}
                    >
                      {config.shortName}
                    </Badge>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* No report state */}
      {!report && !error && (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Trophy className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-medium mb-2">No report generated yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Click "Generate Report" to analyze this site across all 10 evidence dimensions.
              The report will query real data from GSC, GA4, competitive analysis, and more.
            </p>
            <Button onClick={handleGenerate} disabled={generating}>
              <RefreshCw className={`h-4 w-4 mr-1 ${generating ? 'animate-spin' : ''}`} />
              {generating ? 'Generating...' : 'Generate Report'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Dimension cards */}
      {report && report.dimensions.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {report.dimensions.map((dim) => (
            <DimensionCard key={dim.dimensionName} dimension={dim} />
          ))}
        </div>
      )}

      {/* Missing dimensions info */}
      {report && report.dimensionsCompleted < report.dimensionsTotal && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {report.dimensionsTotal - report.dimensionsCompleted} dimension(s) returned no data.
            This is normal for sites that haven't set up all integrations (GA4, GSC, Publisher Manifest, etc.).
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
